import type { Nullable } from '~/types/helpers'
import type { BookingPriceRequest } from '~/types/rental-api-aliases'
import type { BookingEstimateDestination, BookingEstimateParams, RvUsage } from '~/types'
import type { NuxtError } from '#app'

export type CustomQuoteParams = {
  bookingId: number
  renterId: number
  customTotalRentalAmount?: number
  customTotalAddOns?: number
  customDeliveryAmountCents?: number
}

export default function useBookingEstimate() {
  const bookingEstimateParams = useBookingEstimateParams()
  const bookingEstimatePending = useBookingEstimatePending()
  const bookingEstimateError = useBookingEstimateError()
  const bookingEstimateData = useBookingEstimateData()
  const hasPendingDeliveryType = useHasPendingDeliveryType()
  const { $rentals } = useNuxtApp()
  const {
    rvId,
    rvOwnerId,
    rvGuests,
    rvMinimumTripLength,
    rvAllowPets,
    rvHasDelivery,
    rvIsDeliveryOnly,
  } = useRvDetails()

  // Delivery-only listings can't be picked up, so they default to stationary delivery
  const resolvedRvUsage = computed<NonNullable<RvUsage>>(() => {
    return bookingEstimateParams.value.rvUsage ?? (rvIsDeliveryOnly.value ? 'StationaryAfterDelivery' : 'Pickup')
  })

  const availableRvUsages = computed(() => {
    const usages: NonNullable<RvUsage>[] = []

    if (!rvIsDeliveryOnly.value) {
      usages.push('Pickup')
    }

    if (rvHasDelivery.value) {
      if (!rvIsDeliveryOnly.value) {
        usages.push('Delivery')
      }

      usages.push('StationaryAfterDelivery')
    }

    return usages
  })

  // Keep the stored usage when this listing offers it, otherwise fall back to the only option (pickup-only and delivery-only listings)
  const selectedRvUsage = computed<RvUsage>(() => {
    const storedRvUsage = bookingEstimateParams.value.rvUsage

    if (storedRvUsage && availableRvUsages.value.includes(storedRvUsage)) {
      return storedRvUsage
    }

    return availableRvUsages.value.length === 1 ? availableRvUsages.value[0] : null
  })

  const deliveryCostError = useDeliveryCostError()
  const hasDateRange = computed(() => Boolean(bookingEstimateParams.value.dateStart && bookingEstimateParams.value.dateEnd))
  const hasIncompleteDateRange = computed(() => !bookingEstimateParams.value.dateStart || !bookingEstimateParams.value.dateEnd)
  const hasIncompleteDestination = computed(() => {
    return !selectedRvUsage.value
      || !bookingEstimateParams.value.destination
      || (selectedRvUsage.value === 'Delivery' && hasPendingDeliveryType.value)
      || ((selectedRvUsage.value === 'Delivery' || selectedRvUsage.value === 'StationaryAfterDelivery') && Boolean(deliveryCostError.value))
  })
  const isBookable = computed(() => Boolean(bookingEstimateData.value))

  /**
   * Fetch Booking Price Estimate.
   *
   * @param customQuoteParams The custom quote parameters
   * @returns A promise that resolves when the booking estimate is fetched
   */
  async function fetchBookingEstimate(customQuoteParams?: CustomQuoteParams) {
    if (bookingEstimatePending.value || !rvId.value || !rvOwnerId.value) {
      return {
        bookingEstimateData,
        bookingEstimateError,
        bookingEstimatePending,
      }
    }

    if (hasIncompleteDateRange.value) {
      bookingEstimateData.value = null
      return {
        bookingEstimateData,
        bookingEstimateError,
        bookingEstimatePending,
      }
    }

    try {
      bookingEstimatePending.value = true

      // Validate guest count
      if (Number(bookingEstimateParams.value.adults + bookingEstimateParams.value.children) > rvGuests.value) {
        throw createError({
          data: {
            Code: 'INVALID_GUEST_COUNT',
            Message: 'Requested guests count is exceeding RV capacity',
          },
        })
      }

      // Validate minimum trip duration
      const tripDuration = datetime(bookingEstimateParams.value.dateEnd).diff(datetime(bookingEstimateParams.value.dateStart), 'day')
      if (tripDuration < rvMinimumTripLength.value) {
        throw createError({
          data: {
            Code: 'INVALID_TRIP_DURATION',
            Message: 'Requested trip duration is less than the minimum required',
          },
        })
      }

      // Validate Pets friendly rules
      if (bookingEstimateParams.value.pets && !rvAllowPets.value) {
        throw createError({
          data: {
            Code: 'NO_PETS_ALLOWED',
            Message: 'Requested pets is disallowed by RV rules',
          },
        })
      }

      const params = computed(() => <BookingPriceRequest>({
        DateEnd: bookingEstimateParams.value.dateEnd,
        DateStart: bookingEstimateParams.value.dateStart,
        OwnerId: rvOwnerId.value,
        RVId: rvId.value,
        PlaceOfResidence: 'XY',
        ProtectionLevel: bookingEstimateParams.value.protectionLevel,
        RoadsideAssistanceState: bookingEstimateParams.value.roadsideAssistance,
        ListAddOnPicked: bookingEstimateParams.value.addons,
        Adults: bookingEstimateParams.value.adults,
        Children: bookingEstimateParams.value.children,
        Pets: bookingEstimateParams.value.pets ? 1 : 0,
        RvUsage: resolvedRvUsage.value,
        ShowProtectionPlan: true,

        // Custom Quote
        BookingId: customQuoteParams?.bookingId,
        CustomTotalRentalAmount: customQuoteParams?.customTotalRentalAmount,
        CustomTotalAddOnAmount: customQuoteParams?.customTotalAddOns,
        CustomDeliveryAmountCents: customQuoteParams?.customDeliveryAmountCents,
        RenterId: customQuoteParams?.renterId,
      }))

      if (bookingEstimateParams.value.destination) {
        params.value.Destination = bookingEstimateParams.value.destination.name
        params.value.DestinationLatitude = bookingEstimateParams.value.destination.latitude
        params.value.DestinationLongitude = bookingEstimateParams.value.destination.longitude
        params.value.DestinationTypes = bookingEstimateParams.value.destination.types
        params.value.DestinationPlaceId = bookingEstimateParams.value.destination.placeId
      }

      const data = await $rentals('/api/bookings/get-booking-price', {
        method: 'POST',
        body: params.value,
      })

      bookingEstimateData.value = data
      bookingEstimateError.value = null
    }
    catch (err) {
      bookingEstimateData.value = null
      bookingEstimateError.value = createError(err as NuxtError)
    }
    finally {
      bookingEstimatePending.value = false
    }

    return {
      bookingEstimateData,
      bookingEstimateError,
      bookingEstimatePending,
    }
  }

  /**
   * Update Booking Price parameters.
   */
  function updateDateRange({ dateStart, dateEnd }: { dateStart: Nullable<string>, dateEnd: Nullable<string> }) {
    bookingEstimateParams.value.dateStart = dateStart || ''
    bookingEstimateParams.value.dateEnd = dateEnd || ''
  }

  function updateGuests({ adults = 1, children = 0, pets = false }) {
    bookingEstimateParams.value.adults = adults
    bookingEstimateParams.value.children = children
    bookingEstimateParams.value.pets = pets
  }

  function updateRoadsideAssistance(roadsideAssistance: boolean) {
    bookingEstimateParams.value.roadsideAssistance = roadsideAssistance
  }

  function updateProtectionPlan(protectionPlan: BookingPriceRequest['ProtectionLevel']) {
    bookingEstimateParams.value.protectionLevel = protectionPlan ?? null
  }

  function updateRvUsage(rvUsage: RvUsage) {
    bookingEstimateParams.value.rvUsage = rvUsage
  }

  function updateDestination(destination?: BookingEstimateDestination) {
    bookingEstimateParams.value.destination = destination
  }

  /**
   * Update the addons for the booking.
   * @param addons The list of addon IDs to add to the booking.
   */
  function updateAddOns(addons: number[]) {
    const current = bookingEstimateParams.value.addons?.map((addon) => addon.IdInRV) ?? []

    if (current.length === addons.length && current.every((id, i) => id === addons[i])) {
      return
    }

    bookingEstimateParams.value.addons = addons.map((addonId) => ({
      Quantity: 1,
      IdInRV: addonId,
    }))
  }

  function updateEstimateParams(params: BookingEstimateParams) {
    bookingEstimateParams.value = params
  }

  const financialData = computed(() => getFinancial(bookingEstimateData.value?.Financial))

  const rvSnapshot = computed(() => getRvSnapshot(bookingEstimateData.value?.RVSnapshot))

  return {
    bookingEstimateParams,
    bookingEstimateData,
    bookingEstimatePending,
    bookingEstimateError,
    hasDateRange,
    hasIncompleteDateRange,
    hasIncompleteDestination,
    hasPendingDeliveryType,
    isBookable,
    resolvedRvUsage,
    selectedRvUsage,
    ...toRefs(toReactive(financialData)),
    ...toRefs(toReactive(rvSnapshot)),
    fetchBookingEstimate,
    updateAddOns,
    updateDateRange,
    updateDestination,
    updateEstimateParams,
    updateGuests,
    updateProtectionPlan,
    updateRoadsideAssistance,
    updateRvUsage,
  }
}
